import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useTenant } from "@/contexts/TenantContext";
import { Building2, Plus, Palette, Check, Ban, Globe, ExternalLink, Trash2, RefreshCw, Sparkles } from "lucide-react";

const SPORT_TYPES = ["PADEL", "TENNIS", "SQUASH", "PICKLEBALL", "BADMINTON", "SOCCER", "BASKETBALL", "CRICKET", "OTHER"];

const emptyForm = () => ({
  slug: "",
  name: "",
  sport_type: "PADEL",
  logo_url: "",
  domain_aliases: "",
  theme_brand_primary: "#DC2626",
  theme_brand_secondary: "#16A34A",
  theme_brand_accent: "#111111",
});

function slugify(v) {
  return (v || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
}

export default function TenantsPage() {
  const { tenant: activeTenant, activeSlug, switchTenant } = useTenant();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tenants");
      setTenants(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm(emptyForm());
    setEditTenant(null);
    setCreateOpen(true);
  };

  const openEdit = (t) => {
    setEditTenant(t);
    setForm({
      slug: t.slug,
      name: t.name,
      sport_type: t.sport_type,
      logo_url: t.logo_url || "",
      domain_aliases: (t.domain_aliases || []).join(", "),
      theme_brand_primary: t.theme_config?.brand_primary || "#DC2626",
      theme_brand_secondary: t.theme_config?.brand_secondary || "#16A34A",
      theme_brand_accent: t.theme_config?.brand_accent || "#111111",
    });
    setCreateOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        sport_type: form.sport_type,
        logo_url: form.logo_url.trim(),
        domain_aliases: form.domain_aliases
          .split(",").map((s) => s.trim()).filter(Boolean),
        theme_config: {
          brand_primary: form.theme_brand_primary,
          brand_secondary: form.theme_brand_secondary,
          brand_accent: form.theme_brand_accent,
        },
      };
      if (editTenant) {
        await api.patch(`/tenants/${editTenant.id}`, payload);
        toast.success("Tenant updated");
      } else {
        await api.post("/tenants", { slug: form.slug || slugify(form.name), ...payload });
        toast.success("Tenant created");
      }
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false);
  };

  const setStatus = async (t, status) => {
    try {
      await api.patch(`/tenants/${t.id}`, { status });
      toast.success(`Tenant ${status.toLowerCase()}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const archiveTenant = async (t) => {
    if (!window.confirm(`Archive tenant "${t.name}"? This cannot be undone from the UI.`)) return;
    try {
      await api.delete(`/tenants/${t.id}`);
      toast.success("Tenant archived");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const activateTenantView = async (t) => {
    await switchTenant(t.slug);
    toast.success(`Now viewing: ${t.name}`, { description: "Reload the page to see full effect." });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Building2 className="w-8 h-8 text-red-500" />
            Tenants
          </h1>
          <p className="text-zinc-400 mt-1">
            White-label deployments — each tenant has its own branding, rules, users, and data.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} className="border-zinc-700 text-zinc-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700">
            <Plus className="w-4 h-4 mr-2" /> New tenant
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-400">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {tenants.map((t) => {
            const isActive = activeSlug ? t.slug === activeSlug : t.is_default;
            const isSuspended = t.status === "SUSPENDED";
            const isArchived = t.status === "ARCHIVED";
            return (
              <Card key={t.id} className="p-5 bg-zinc-900/70 border-zinc-800 space-y-4">
                <div className="flex items-start gap-3">
                  <div
                    className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-black"
                    style={{ background: t.theme_config?.brand_primary || "#DC2626" }}
                  >
                    {t.name?.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-bold text-white truncate">{t.name}</h3>
                      {t.is_default && <Badge className="bg-red-600 text-white">DEFAULT</Badge>}
                      {isActive && <Badge className="bg-emerald-600 text-white">VIEWING</Badge>}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 font-mono truncate">/{t.slug}</div>
                    <div className="text-xs text-zinc-400 mt-1">{t.sport_type}</div>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      isSuspended ? "border-amber-600 text-amber-500"
                      : isArchived ? "border-zinc-600 text-zinc-500"
                      : "border-emerald-700 text-emerald-500"
                    }
                  >
                    {t.status}
                  </Badge>
                </div>

                {/* Theme swatches */}
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-zinc-500" />
                  {["brand_primary", "brand_secondary", "brand_accent"].map((k) => (
                    <div
                      key={k}
                      className="w-6 h-6 rounded border border-zinc-700"
                      style={{ background: t.theme_config?.[k] }}
                      title={`${k}: ${t.theme_config?.[k]}`}
                    />
                  ))}
                </div>

                {t.domain_aliases?.length ? (
                  <div className="flex items-start gap-2 text-xs text-zinc-400">
                    <Globe className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <div className="break-all">{t.domain_aliases.join(", ")}</div>
                  </div>
                ) : null}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-800">
                  <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-200"
                    onClick={() => openEdit(t)}>
                    Edit
                  </Button>
                  {!isActive && (
                    <Button size="sm" variant="outline" className="border-emerald-800 text-emerald-400"
                      onClick={() => activateTenantView(t)}>
                      <Sparkles className="w-3 h-3 mr-1" /> View as
                    </Button>
                  )}
                  {t.status === "ACTIVE" && !t.is_default && (
                    <Button size="sm" variant="outline" className="border-amber-800 text-amber-500"
                      onClick={() => setStatus(t, "SUSPENDED")}>
                      <Ban className="w-3 h-3 mr-1" /> Suspend
                    </Button>
                  )}
                  {t.status === "SUSPENDED" && (
                    <Button size="sm" variant="outline" className="border-emerald-800 text-emerald-400"
                      onClick={() => setStatus(t, "ACTIVE")}>
                      <Check className="w-3 h-3 mr-1" /> Activate
                    </Button>
                  )}
                  {!t.is_default && (
                    <Button size="sm" variant="outline" className="border-red-900 text-red-500"
                      onClick={() => archiveTenant(t)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Archive
                    </Button>
                  )}
                  <a href={`/?tenant=${t.slug}`} target="_blank" rel="noreferrer"
                    className="ml-auto text-xs text-zinc-500 hover:text-zinc-300 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Public site
                  </a>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editTenant ? "Edit tenant" : "New tenant"}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Each tenant is an isolated white-label deployment with its own users, teams,
              players, and branding.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-zinc-300">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value, slug: editTenant ? form.slug : slugify(e.target.value) })}
                placeholder="Kenya Tennis Federation"
                className="bg-zinc-900 border-zinc-700 text-white"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Slug (URL identifier)</Label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                placeholder="kenya-tennis"
                disabled={!!editTenant}
                className="bg-zinc-900 border-zinc-700 text-white font-mono"
              />
              {!editTenant && <p className="text-xs text-zinc-500 mt-1">Cannot be changed later.</p>}
            </div>
            <div>
              <Label className="text-zinc-300">Sport</Label>
              <Select value={form.sport_type} onValueChange={(v) => setForm({ ...form, sport_type: v })}>
                <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-white">
                  {SPORT_TYPES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-300">Logo URL</Label>
              <Input
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="/assets/tenant-logo.png"
                className="bg-zinc-900 border-zinc-700 text-white"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-zinc-300">Domain aliases (comma-separated)</Label>
              <Input
                value={form.domain_aliases}
                onChange={(e) => setForm({ ...form, domain_aliases: e.target.value })}
                placeholder="tennis.example.com, kenyatennis.co.ke"
                className="bg-zinc-900 border-zinc-700 text-white"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Requests to these hostnames automatically resolve to this tenant.
              </p>
            </div>

            {/* Theme colors */}
            <div className="col-span-2 border-t border-zinc-800 pt-4">
              <Label className="text-zinc-300 flex items-center gap-2">
                <Palette className="w-4 h-4" /> Brand colors
              </Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  ["theme_brand_primary", "Primary"],
                  ["theme_brand_secondary", "Secondary"],
                  ["theme_brand_accent", "Accent"],
                ].map(([k, lbl]) => (
                  <div key={k}>
                    <div className="text-xs text-zinc-400 mb-1">{lbl}</div>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        className="w-10 h-10 rounded border border-zinc-700 bg-zinc-900"
                      />
                      <Input
                        value={form[k]}
                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                        className="bg-zinc-900 border-zinc-700 text-white font-mono text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-zinc-700 text-zinc-300">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving || !form.name} className="bg-red-600 hover:bg-red-700">
              {saving ? "Saving…" : editTenant ? "Save changes" : "Create tenant"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
